import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { PITCH_DECK_GENERATION_KEY } from "@/hooks/usePitchDeckGeneration";

interface GenerateShareTokenParams {
  pitchDeckId: string;
  /** Omit together with neverExpires to accept the server default (30 days). */
  expiresInDays?: number;
  /** Explicit opt-in to a permanent, non-expiring link. */
  neverExpires?: boolean;
}

interface ShareTokenResponse {
  success: boolean;
  share_token?: string;
  share_url?: string;
  expires_at?: string | null;
  error?: string;
}

export const usePitchDeckSharing = () => {
  const queryClient = useQueryClient();

  const generateShareToken = useMutation({
    mutationFn: async ({ pitchDeckId, expiresInDays, neverExpires }: GenerateShareTokenParams): Promise<ShareTokenResponse> => {
      const { data, error } = await supabase.functions.invoke(
        "generate-pitch-deck-share-token",
        {
          body: {
            pitch_deck_id: pitchDeckId,
            expires_in_days: expiresInDays ?? null,
            never_expires: neverExpires === true,
          },
        }
      );

      if (error) throw error;
      if (!data.success) throw new Error(data.error || "Failed to generate share link");
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Share link generated",
        description: "Your pitch deck can now be shared publicly",
      });
      queryClient.invalidateQueries({ queryKey: ["pitch-decks"] });
      queryClient.invalidateQueries({ queryKey: [PITCH_DECK_GENERATION_KEY] });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to generate share link",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const revokeShareToken = useMutation({
    mutationFn: async (pitchDeckId: string) => {
      const { data, error } = await supabase.functions.invoke(
        "revoke-pitch-deck-share-token",
        {
          body: { pitch_deck_id: pitchDeckId },
        }
      );

      if (error) throw error;
      if (!data.success) throw new Error(data.error || "Failed to revoke share link");
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Share link revoked",
        description: "This pitch deck is no longer publicly accessible",
      });
      queryClient.invalidateQueries({ queryKey: ["pitch-decks"] });
      queryClient.invalidateQueries({ queryKey: [PITCH_DECK_GENERATION_KEY] });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to revoke share link",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    generateShareToken,
    revokeShareToken,
  };
};
